<script setup>
//CampaignConfigureListBaica.vue
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { defineAsyncComponent, useTemplateRef } from 'vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import ReplyBottomPanel from 'dashboard/components/widgets/WootWriter/ReplyBottomPanel.vue';
import AttachmentsPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'dashboard/components-shadcn/new-york/ui/tabs';
import { ArrowLeft, ArrowRight, MessageSquare } from 'lucide-vue-next';
import { useRouter } from 'vue-router';

// Carregamento dinâmico do EmojiInput
const EmojiInput = defineAsyncComponent(() => import('shared/components/emoji/EmojiInput.vue'));

const { t } = useI18n();

const store = useStore();
const router = useRouter();

const labels = useMapGetter('labels/getLabels');
const allInboxes = useMapGetter('inboxes/getInboxes');
const whatsappAppInboxes = computed(() =>
  (allInboxes.value || []).filter(
    i =>
      i.channel_type === 'Channel::Api' ||
      (i.channel_type === 'Channel::Whatsapp' && i.provider === 'baileys')
  )
);
const whatsappAppInbox = computed(() => whatsappAppInboxes.value[0] || null);

// Transformar labels para o formato do TagMultiSelectComboBox
const labelOptions = computed(() => 
  labels.value.map(label => ({
    value: label.id,
    label: label.title
  }))
);

// Transformar whatsappAppInbox para o formato do ComboBox
const inboxOptions = computed(() =>
  whatsappAppInboxes.value.map(i => ({
    value: i.id,
    label: i.name,
  }))
);

// ✅ Usar os novos getters do store para WhatsApp normal
const campaignData = computed(() => store.getters['campaigns/getWhatsappNormalCampaignData']);
const currentStep = computed(() => store.getters['campaigns/getWhatsappNormalCurrentStep']);

// Estados para os componentes - inicializar com dados do store
const selectedLabels = ref(campaignData.value.selectedLabels || []);
const selectedInbox = ref(campaignData.value.selectedInbox || whatsappAppInbox.value?.id || '');
const webhookUrl = ref(campaignData.value.webhookUrl || '');

// Estados para o editor de mensagem - inicializar com dados do store
const message = ref(campaignData.value.message || '');
const attachedFiles = ref(campaignData.value.attachedFiles || []);
const showEmojiPicker = ref(false);
const isRecordingAudio = ref(false);
const recordingAudioState = ref('');
const recordingAudioDurationText = ref('00:00');
const isRecorderAudioStopped = ref(true);
const editorRef = ref(null);
const audioRecorderRef = useTemplateRef('audioRecorderInput');

// Estados das tabs - inicializar com dados do store
const activeTab = ref(currentStep.value || 'config');
const tabValidation = ref({
  config: store.getters['campaigns/isWhatsappNormalConfigValid'],
  message: store.getters['campaigns/isWhatsappNormalMessageValid']
});

// Computed properties para o editor
const isPrivate = computed(() => false);
const isOnPrivateNote = computed(() => true);
const showFileUpload = computed(() => true);
const showAudioRecorder = computed(() => true);
const maxLength = computed(() => 160);
const hasWhatsappTemplates = computed(() => false);
const conversationId = computed(() => 0);
const portalSlug = computed(() => '');
const inbox = computed(() => whatsappAppInbox.value || { id: 0, channel_type: 'Channel::Api' });
const channelType = computed(() => 'Channel::Api');
const messagePlaceHolder = computed(() => t('CAMPAIGN.SMS.CREATE.FORM.MESSAGE.PLACEHOLDER'));
const replyButtonLabel = computed(() => t('CONVERSATION.REPLYBOX.SEND') + ' (↵)');

// Validação das tabs
const isConfigValid = computed(() => {
  return selectedLabels.value.length > 0 && selectedInbox.value && webhookUrl.value;
});

const isMessageValid = computed(() => {
  return message.value.length > 0 || attachedFiles.value.length > 0;
});

// Navegação das tabs
const canNavigateToMessage = computed(() => {
  return tabValidation.value.config;
});

// Header content
const headerContent = computed(() => {
  return t('Preencha as informações abaixo para configurar a campanha');
});

// ✅ Função para salvar dados no store (WhatsApp normal)
const saveCampaignData = () => {
  store.dispatch('campaigns/saveWhatsappNormalCampaignData', {
    selectedLabels: selectedLabels.value,
    selectedInbox: selectedInbox.value,
    webhookUrl: webhookUrl.value,
    message: message.value,
    attachedFiles: attachedFiles.value
  });
};

// Funções para o editor de mensagem
const toggleEmojiPicker = () => {
  showEmojiPicker.value = !showEmojiPicker.value;
};

const addEmoji = (emoji) => {
  if (!editorRef.value) {
    message.value = (message.value || '') + emoji;
    toggleEmojiPicker();
    saveCampaignData(); // Salvar após mudança
    return;
  }

  // Tentar acessar a instância real do editor através do proxy
  const editor = editorRef.value.$el || editorRef.value.editor || editorRef.value;
  
  if (editor && editor.insertSpecialContentByTemplate) {
    try {
      editor.insertSpecialContentByTemplate('emoji', emoji);
      toggleEmojiPicker();
      saveCampaignData(); // Salvar após mudança
      return;
    } catch (error) {
      console.error('Erro:', error);
    }
  }

  // Fallback: inserir diretamente no v-model
  message.value = (message.value || '') + emoji;
  toggleEmojiPicker();
  saveCampaignData(); // Salvar após mudança
};



const onFileUpload = (file) => {
  let thumb = '';
  if (file.file) {
    thumb = URL.createObjectURL(file.file);
  }

  attachedFiles.value.push({
    resource: file,
    isPrivate: false,
    thumb: thumb,
    isRecordedAudio: file.isRecordedAudio || false,
  });
  
  saveCampaignData(); // Salvar após mudança
};

const removeAttachment = (attachments) => {
  attachedFiles.value = attachments;
  saveCampaignData(); // Salvar após mudança
};

const onRecordProgressChanged = (duration) => {
  recordingAudioDurationText.value = duration;
};

const onFinishRecorder = (file) => {
  recordingAudioState.value = 'stopped';
  isRecorderAudioStopped.value = true;
  if (file) {
    onFileUpload({ ...file, isRecordedAudio: true });
  }
};

const toggleAudioRecorder = () => {
  isRecordingAudio.value = !isRecordingAudio.value;
  isRecorderAudioStopped.value = !isRecordingAudio.value;
  if (!isRecordingAudio.value) {
    recordingAudioDurationText.value = '00:00';
    recordingAudioState.value = '';
    if (audioRecorderRef.value) {
      audioRecorderRef.value.stopRecording();
    }
  }
};

const toggleAudioRecorderPlayPause = () => {
  if (!isRecordingAudio.value) return;
  if (!isRecorderAudioStopped.value) {
    isRecorderAudioStopped.value = true;
    recordingAudioState.value = 'stopped';
    if (audioRecorderRef.value) {
      audioRecorderRef.value.stopRecording();
    }
  } else {
    recordingAudioState.value = recordingAudioState.value === 'playing' ? 'paused' : 'playing';
    if (audioRecorderRef.value) {
      audioRecorderRef.value.playPause();
    }
  }
};

// Funções de navegação
const onTabChange = (newTab) => {
  if (newTab === 'message' && !canNavigateToMessage.value) {
    return;
  }
  activeTab.value = newTab;
  store.dispatch('campaigns/setWhatsappNormalCurrentStep', newTab);
  saveCampaignData(); // Salvar antes de trocar de aba
};

const nextTab = () => {
  if (activeTab.value === 'config' && isConfigValid.value) {
    saveCampaignData(); // Salvar antes de avançar
    tabValidation.value.config = true;
    activeTab.value = 'message';
    store.dispatch('campaigns/setWhatsappNormalCurrentStep', 'message');
  }
};

const previousTab = () => {
  if (activeTab.value === 'message') {
    saveCampaignData(); // Salvar antes de voltar
    activeTab.value = 'config';
    store.dispatch('campaigns/setWhatsappNormalCurrentStep', 'config');
  }
};

const handleSubmit = async () => {
  saveCampaignData(); // Salvar antes de navegar

  router.push({
    name: 'campaigns_whatsapp_app_schedule_step',
    params: { accountId: store.state.auth.accountId },
  });
};

// Watchers para salvar dados automaticamente
watch([selectedLabels, selectedInbox, webhookUrl], () => {
  saveCampaignData();
}, { deep: true });

watch(message, () => {
  saveCampaignData();
});

// Watchers para validação
watch(isConfigValid, (newValue) => {
  if (newValue) {
    tabValidation.value.config = true;
  }
}, { immediate: true });

watch(isMessageValid, (newValue) => {
  tabValidation.value.message = newValue;
}, { immediate: true });

// Salvar dados antes de sair da página
const handleBeforeUnload = () => {
  saveCampaignData();
};

onMounted(() => {
  
  // Adicionar listener para salvar dados antes de sair
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  // Carregar dados do store se existirem
  const storedData = campaignData.value;
  if (storedData) {
    selectedLabels.value = storedData.selectedLabels || [];
    selectedInbox.value = storedData.selectedInbox || whatsappAppInbox.value?.id || '';
    webhookUrl.value = storedData.webhookUrl || '';
    message.value = storedData.message || '';
    attachedFiles.value = storedData.attachedFiles || [];
  }
  

});

// Cleanup
onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});

defineOptions({ name: 'CampaignConfigureList' });
</script>

<template>
  <div class="campaign-container">
    
    <!-- Header simples -->
    <div class="header-section">
      <PageHeader
        :header-title="$t('Seleção do Publico')"
        :header-content="headerContent"
      />
    </div>
    
    <!-- Tabs Container -->
    <div>
      <Tabs
        :model-value="activeTab"
        @update:model-value="onTabChange"
        class="w-full"
      >
        <!-- Tab Navigation -->
        <TabsList class="tab-list">
          <TabsTrigger
            value="config"
            :class="{ 'tab-disabled': !canNavigateToMessage }"
          >
            <div class="tab-content">
              <span class="tab-icon">⚙️</span>
              <span class="tab-label-full">Configuração</span>
              <span class="tab-label-short">Config</span>
              <span v-if="tabValidation.config" class="tab-check">✓</span>
            </div>
          </TabsTrigger>

          <TabsTrigger
            value="message"
            :disabled="!canNavigateToMessage"
            :class="{ 'tab-disabled': !canNavigateToMessage }"
          >
            <div class="tab-content">
              <span class="tab-icon">💬</span>
              <span class="tab-label-full">Mensagem</span>
              <span class="tab-label-short">Msg</span>
              <span 
                v-if="tabValidation.message" 
                class="tab-check"
              >
                ✓
              </span>
            </div>
          </TabsTrigger>
        </TabsList>

        <!-- Tab Content -->

          
          <!-- Tab 1: Configuração -->
          <TabsContent value="config" class="tab-content-area">
            <div class="modern-tab-container">
              <!-- Decorative Background Elements -->
              <div class="background-decoration">
                <div class="decoration-blob decoration-blob-1"></div>
                <div class="decoration-blob decoration-blob-2"></div>
              </div>

              <!-- Header Section -->
              <div class="tab-header">
                <div class="header-content">
                  <div class="accent-line"></div>
                  <div class="min-w-0 flex-1">
                    <h3 class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2">Configuração da Campanha</h3>
                    <p class="description-text">Configure as labels, inbox e webhook para sua campanha</p>
                  </div>
                </div>
              </div>

              <!-- Main Content Area -->
              <div class="main-content-area">
                <!-- Selection Cards -->
                <div class="selection-section">
                  <div class="form-card">
                    <div class="form-header">
                      <h3 class="form-title">
                        <span class="form-icon">🏷️</span>
                        Selecionar Labels
                      </h3>
                      <p class="form-description">Escolha as labels para segmentar sua audiência</p>
                    </div>
                    <div class="form-content">
                      <TagMultiSelectComboBox
                        v-model="selectedLabels"
                        :options="labelOptions"
                        placeholder="Escolha as labels..."
                        search-placeholder="Buscar labels..."
                        empty-state="Nenhuma label encontrada"
                      />
                    </div>
                  </div>

                  <div class="form-card">
                    <div class="form-header">
                      <h3 class="form-title">
                        <span class="form-icon">💬</span>
                        WhatsApp Inbox
                      </h3>
                      <p class="form-description">Configure o canal de envio</p>
                    </div>
                    <div class="form-content">
                      <ComboBox
                        v-model="selectedInbox"
                        :options="inboxOptions"
                        placeholder="WhatsApp Inbox..."
                        :disabled="!whatsappAppInbox"
                      />
                    </div>
                  </div>
                </div>

                <!-- Webhook Section -->
                <div class="form-card">
                  <div class="form-header">
                    <h3 class="form-title">
                      <span class="form-icon">🔗</span>
                      Webhook URL
                    </h3>
                    <p class="form-description">URL para receber notificações da campanha</p>
                  </div>
                  <div class="form-content">
                    <input
                      v-model="webhookUrl"
                      type="url"
                      placeholder="https://exemplo.com/webhook"
                      class="form-input"
                    />
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-bar-background"></div>
                
                <!-- Back Button (empty for first tab) -->
                <div class="back-section"></div>

                <!-- Center Actions -->
                <div class="center-actions"></div>

                <!-- Primary Actions -->
                <div class="primary-actions">
                  <button
                    type="button"
                    @click="nextTab"
                    :disabled="!isConfigValid"
                    class="modern-button primary-button"
                  >
                    <div class="primary-gradient"></div>
                    <div class="primary-glow"></div>
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline">Criar Mensagem</span>
                        <span class="sm:hidden">Mensagem</span>
                        <MessageSquare class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          <!-- Tab 2: Mensagem -->
          <TabsContent value="message" class="tab-content-area">
            <div class="modern-tab-container h-full">
              <!-- Decorative Background Elements -->
              <div class="background-decoration">
                <div class="decoration-blob decoration-blob-1"></div>
                <div class="decoration-blob decoration-blob-2"></div>
              </div>

              <!-- Header Section -->
              <div class="tab-header">
                <div class="header-content">
                  <div class="accent-line"></div>
                  <div class="min-w-0 flex-1">
                    <h3 class="text-lg font-semibold text-n-slate-12 dark:text-white mb-2">Mensagem da Campanha</h3>
                    <p class="description-text">Crie o conteúdo que será enviado para seus contatos</p>
                  </div>
                </div>
              </div>

              <!-- Main Content Area -->
              <div class="main-content-area flex-1 overflow-y-auto">
                <!-- Message Editor -->
                <div class="form-card editor-card">
                  
                  <div class="form-content">
                    <div class="reply-box">
                      <div class="reply-box__top">
                        <AudioRecorder
                          v-if="showAudioRecorder && isRecordingAudio"
                          ref="audioRecorderInput"
                          :audio-record-format="AUDIO_FORMATS.MP3"
                          :inbox-id="inbox.id"
                          @recorder-progress-changed="onRecordProgressChanged"
                          @finish-record="onFinishRecorder"
                          @play="recordingAudioState = 'playing'"
                          @pause="recordingAudioState = 'paused'"
                        />
                        <WootMessageEditor
                          v-else
                          ref="editorRef"
                          v-model="message"
                          :editor-id="'campaign-editor'"
                          :placeholder="messagePlaceHolder"
                          :is-private="isPrivate"
                          :channel-type="channelType"
                          :max-length="maxLength"
                          enable-variables
                          :variables="{}"
                        />
                        <EmojiInput
                          v-if="showEmojiPicker"
                          :class="['emoji-dialog', 'absolute', 'z-10', '-top-9', 'bottom-0', 'left-0']"
                          :on-click="addEmoji"
                        />
                      </div>
                      
                      <div v-if="attachedFiles.length > 0" class="attachments-section">
                        <AttachmentsPreview
                          :attachments="attachedFiles"
                          @remove-attachment="removeAttachment"
                        />
                      </div>
                      
                      <ReplyBottomPanel
                        :conversation-id="conversationId"
                        :enable-multiple-file-upload="true"
                        :has-whatsapp-templates="hasWhatsappTemplates"
                        :inbox="inbox"
                        :is-on-private-note="isOnPrivateNote"
                        :is-recording-audio="isRecordingAudio"
                        :is-send-disabled="message.length === 0 && attachedFiles.length === 0"
                        :mode="'reply'"
                        :on-file-upload="onFileUpload"
                        :recording-audio-duration-text="recordingAudioDurationText"
                        :recording-audio-state="recordingAudioState"
                        :send-button-text="replyButtonLabel"
                        :show-audio-recorder="showAudioRecorder"
                        :show-emoji-picker="showEmojiPicker"
                        :show-file-upload="showFileUpload"
                        :show-send-button="false"
                        :show-ai-assistance-button="false"
                        :show-ai-quick-actions="false"
                        :toggle-audio-recorder-play-pause="toggleAudioRecorderPlayPause"
                        :toggle-audio-recorder="toggleAudioRecorder"
                        :toggle-emoji-picker="toggleEmojiPicker"
                        :message="message"
                        :portal-slug="portalSlug"
                        :new-conversation-modal-active="false"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-bar-background"></div>
                
                <!-- Back Button -->
                <div class="back-section">
                  <button
                    type="button"
                    @click="previousTab"
                    class="modern-button back-button"
                  >
                    <div class="button-shine"></div>
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline">Retornar</span>
                    <span class="sm:hidden">Voltar</span>
                  </button>
                </div>

                <!-- Center Actions -->
                <div class="center-actions"></div>

                <!-- Primary Actions -->
                <div class="primary-actions">
                  <button
                    type="button"
                    @click="handleSubmit"
                    :disabled="!isMessageValid"
                    class="modern-button primary-button"
                  >
                    <div class="primary-gradient"></div>
                    <div class="primary-glow"></div>
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline">Agendamento</span>
                        <span class="sm:hidden">Agendamento</span>
                        <ArrowRight class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        
      </Tabs>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.campaign-container {
  @apply border border-woot-400 bg-n-background min-h-[100px] h-full p-6 w-full max-w-full overflow-y-auto;
  @apply md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0;
}

.header-section {
  @apply mb-4 sm:mb-6;
}

.tab-list {
  @apply grid grid-cols-2 p-1 rounded-lg border border-woot-400 bg-white dark:bg-slate-900 w-full;
}

.tab-content {
  @apply flex items-center justify-center gap-1 sm:gap-2 min-w-0;
}

.tab-icon {
  @apply text-sm sm:text-base flex-shrink-0;
}

.tab-label-full {
  @apply hidden sm:block font-medium;
}

.tab-label-short {
  @apply sm:hidden truncate;
}

.tab-check {
  @apply text-green-500 dark:text-green-400 flex-shrink-0;
}

.selection-section {
  @apply grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4;
}

.form-card {
  @apply bg-n-solid-1 border border-woot-400 rounded-xl p-6 shadow-sm;
  @apply transition-all duration-200 hover:shadow-md hover:border-n-blue-6;
  overflow: visible;
}



.form-header {
  @apply mb-4;
}

.form-title {
  @apply text-n-slate-12 font-semibold text-lg mb-2 flex items-center gap-3;
}

.form-icon {
  @apply text-xl;
}

.form-description {
  @apply text-n-slate-11 text-sm;
}

.form-content {
  overflow: visible;
}



.form-input {
  @apply w-full px-4 py-3 bg-n-solid-1 border border-n-weak rounded-lg text-n-slate-12 placeholder-n-slate-10;
  @apply transition-all duration-200 focus:outline-none focus:border-n-brand-ink focus:ring-2 focus:ring-n-blue-3;
}

.reply-box__top {
  @apply relative;
  :deep(.ProseMirror) {
    @apply min-h-[200px] max-h-[400px] overflow-y-auto p-4 text-n-slate-12 bg-transparent border-none outline-none;
  }
  :deep(.ProseMirror-woot-style) {
    @apply min-h-[200px];
  }
}

.attachments-section {
  @apply bg-n-solid-2 border-t border-n-weak p-4;
}

.emoji-picker {
  @apply absolute bottom-0 left-0 z-50 bg-n-solid-1 border border-n-weak rounded-xl shadow-xl;
}

.modern-tab-container {
  @apply relative min-h-[520px] flex flex-col rounded-[20px] border border-woot-400 shadow-lg overflow-hidden;
  background: linear-gradient(135deg, rgb(var(--solid-1)) 0%, rgb(var(--solid-2)) 50%, rgb(var(--solid-1)) 100%);
  box-shadow: 0 20px 40px rgb(var(--slate-4) / 0.1), 0 1px 3px rgb(var(--slate-6) / 0.1);
}

.background-decoration {
  @apply absolute inset-0 pointer-events-none overflow-hidden;
}

.decoration-blob {
  @apply absolute rounded-full opacity-60;
  filter: blur(40px);
  animation: float 8s ease-in-out infinite;
}

.decoration-blob-1 {
  @apply w-[200px] h-[200px] -top-[50px] -right-[50px];
  background: radial-gradient(circle, rgb(var(--teal-5) / 0.3), transparent);
}

.decoration-blob-2 {
  @apply w-[150px] h-[150px] -bottom-[30px] -left-[30px];
  background: radial-gradient(circle, rgb(var(--iris-5) / 0.25), transparent);
  animation-delay: 4s;
}

.tab-header {
  @apply relative p-4 mt-4 ml-4;
}

.header-content {
  @apply relative flex items-start gap-4;
}

.accent-line {
  @apply w-1 h-[60px] rounded-sm;
  background: linear-gradient(180deg, rgb(var(--teal-9)), rgb(var(--iris-9)));
  animation: pulse 3s ease-in-out infinite;
}

.description-text {
  @apply text-base leading-relaxed font-medium flex-1;
  color: rgb(var(--slate-11));
  background: linear-gradient(135deg, rgb(var(--slate-11)), rgb(var(--slate-9)));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.main-content-area {
  @apply flex-1 relative z-10 p-[12px_32px];
}

.action-bar {
  @apply relative flex items-center justify-between gap-4 rounded-b-2xl p-[20px_32px] m-0 mb-1;
  background: linear-gradient(135deg, rgb(var(--solid-2) / 0.9), rgb(var(--solid-3) / 0.8));
  border-top: 1px solid rgb(var(--teal-6) / 0.3);
  backdrop-filter: blur(10px);
}

.action-bar-background {
  @apply absolute inset-0 pointer-events-none;
  background: radial-gradient(circle at 0% 50%, rgb(var(--teal-4) / 0.1), transparent 50%),
              radial-gradient(circle at 100% 50%, rgb(var(--iris-4) / 0.1), transparent 50%);
}

.back-section, .primary-actions {
  @apply flex-shrink-0;
}

.center-actions {
  @apply flex-1 flex justify-center items-center;
}

.primary-actions {
  @apply flex gap-3;
}

.modern-button {
  @apply relative cursor-pointer border-none flex items-center justify-center gap-2 overflow-hidden;
  @apply h-12 px-6 rounded-xl font-semibold text-sm min-w-[140px] transition-all duration-300;
}

.modern-button:disabled {
  @apply opacity-50 cursor-not-allowed;
}

.button-shine {
  @apply absolute -top-1/2 -left-1/2 w-[200%] h-[200%] pointer-events-none;
  background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%);
  transform: translateX(-100%);
  transition: transform 0.6s;
}

.modern-button:hover .button-shine {
  transform: translateX(100%);
}

.back-button {
  @apply border border-n-weak;
  background: linear-gradient(135deg, rgb(var(--solid-2)), rgb(var(--solid-3)));
  color: rgb(var(--slate-11));
}

.back-button:hover {
  background: linear-gradient(135deg, rgb(var(--solid-3)), rgb(var(--slate-3)));
  border-color: rgb(var(--slate-6) / 0.6);
  transform: translateX(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgb(var(--slate-4) / 0.2);
}

.primary-button {
  @apply relative text-white font-bold border-none;
}

.primary-gradient {
  @apply absolute inset-0 rounded-xl transition-all duration-300;
  background: linear-gradient(135deg, rgb(var(--teal-9)), rgb(var(--teal-10)), rgb(var(--teal-11)));
}

.primary-glow {
  @apply absolute -z-10 rounded-2xl opacity-0 transition-opacity duration-300;
  inset: -4px;
  background: linear-gradient(135deg, rgb(var(--teal-6) / 0.6), rgb(var(--teal-8) / 0.4));
  filter: blur(12px);
}

.button-content {
  @apply relative z-10 flex items-center justify-center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.primary-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.primary-button:hover .primary-gradient {
  background: linear-gradient(135deg, rgb(var(--teal-10)), rgb(var(--teal-11)), rgb(var(--teal-12)));
  animation: gradient-shift 2s ease-in-out infinite;
}

.primary-button:hover .primary-glow {
  @apply opacity-100;
}

.primary-button:active {
  transform: translateY(0) scale(1.02);
}

.button-icon {
  @apply w-4 h-4 transition-transform duration-300;
}

.button-text {
  @apply flex items-center gap-2;
}

.back-button:hover .button-icon {
  transform: translateX(-2px);
}

.primary-button:hover .button-icon {
  transform: translateX(2px) scale(1.1);
}

@keyframes float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
}

@keyframes pulse {
  0%, 100% { opacity: 0.8; transform: scaleY(1); }
  50% { opacity: 1; transform: scaleY(1.1); }
}

@keyframes gradient-shift {
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
}

@media (max-width: 1024px) {
  .campaign-container {
    @apply p-4;
  }
  .selection-section {
    @apply grid-cols-1 gap-4 mb-4;
  }
  .form-card {
    @apply p-4;
  }
}

@media (max-width: 640px) {
  .campaign-container {
    @apply p-3 w-full;
  }
  .form-card {
    @apply p-4;
  }
  .reply-box__top :deep(.ProseMirror) {
    @apply min-h-[160px] p-3;
  }
  .modern-tab-container {
    @apply min-h-[480px] rounded-2xl;
  }
  .action-bar {
    @apply flex-col gap-3;
  }
  .back-section, .primary-actions {
    @apply w-full;
  }
  .primary-actions {
    @apply flex-col;
  }
  .modern-button {
    @apply min-w-full h-[52px] text-base;
  }
  .description-text {
    @apply text-[15px];
  }
}

@media (prefers-reduced-motion: no-preference) {
  .modern-button {
    @apply transition-all duration-200 ease-in-out;
  }
}

@media (prefers-reduced-motion: reduce) {
  .modern-button {
    @apply transition-none;
  }
}

@media (prefers-color-scheme: dark) {
  .modern-tab-container {
    background: linear-gradient(135deg, rgb(var(--solid-1) / 0.95) 0%, rgb(var(--solid-2) / 0.9) 50%, rgb(var(--solid-1) / 0.95) 100%);
    box-shadow: 0 20px 40px rgb(var(--slate-12) / 0.3), 0 1px 3px rgb(var(--slate-12) / 0.2);
  }
  .description-text {
    color: rgb(var(--slate-9));
    background: linear-gradient(135deg, rgb(var(--slate-9)), rgb(var(--slate-8)));
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .action-bar {
    background: linear-gradient(135deg, rgb(var(--solid-2) / 0.8), rgb(var(--solid-3) / 0.7));
    border-top-color: rgb(var(--teal-7) / 0.4);
  }
  .back-button {
    color: rgb(var(--slate-9));
  }
}

:deep(.combobox-dropdown) {
  @apply bg-white opacity-100;
}

@media (prefers-color-scheme: dark) {
  :deep(.combobox-dropdown) {
    @apply bg-[#1a1a1a];
  }
}
</style>